import type { VoiceCommandResult } from './voiceCommandClient';

const CLARIFY_ACTIONS = new Set(['clarify', 'confirm', 'navigate', 'cancelled', 'help', 'unknown']);

/** Maps established voice intents to the page where the user is working. */
const INTENT_ROUTE_MAP: Record<string, string> = {
  create_quote: '/quotes',
  send_quote: '/quotes',
  convert_quote_to_invoice: '/quotes',
  list_quotes: '/quotes',
  create_sales_invoice: '/invoices',
  create_invoice: '/invoices',
  create_purchase_invoice: '/invoices',
  create_bill: '/invoices',
  list_invoices: '/invoices',
  list_bills: '/invoices',
  update_invoice: '/invoices',
  delete_invoice: '/invoices',
  void_invoice: '/invoices',
  create_expense: '/expenses',
  record_expense: '/expenses',
  record_travel_expense: '/expenses',
  record_office_expense: '/expenses',
  update_expense: '/expenses',
  list_expenses: '/expenses',
  create_contact: '/contacts',
  create_customer: '/contacts',
  create_supplier: '/contacts',
  update_contact: '/contacts',
  list_contacts: '/contacts',
  search_contact: '/contacts',
  add_inventory: '/inventory',
  remove_inventory: '/inventory',
  adjust_stock: '/inventory',
  create_inventory_item: '/inventory',
  list_inventory: '/inventory',
  search_inventory: '/inventory',
  create_journal_entry: '/journal-entries',
  update_journal_entry: '/journal-entries',
  create_chart_account: '/chart-of-accounts',
  create_bank_account: '/bank-accounts',
  record_payment: '/bulk-payments',
  record_customer_payment: '/bulk-payments',
  record_payment_received: '/bulk-payments',
  record_supplier_payment: '/bulk-payments',
  record_payment_made: '/bulk-payments',
  record_transfer: '/account-transfers',
  process_document: '/image-processing',
  view_reports: '/reports',
  view_report_pl: '/reports',
  view_report_bs: '/reports',
  view_trial_balance: '/reports',
  view_cash_flow: '/reports',
  create_tax_period: '/tax-periods',
  list_tax_periods: '/tax-periods',
};

export const resolveIntentRoute = (intent?: string | null): string | null => {
  if (!intent) return null;
  return INTENT_ROUTE_MAP[intent] || null;
};

export const extractEstablishedIntent = (result: VoiceCommandResult): string | null => {
  const fromData = result.data?.intent;
  if (typeof fromData === 'string' && fromData) return fromData;

  const conversationState = (result as VoiceCommandResult & {
    conversation_state?: { original_intent?: string; current_intent?: string };
  }).conversation_state;

  if (conversationState?.original_intent) return conversationState.original_intent;
  if (conversationState?.current_intent) return conversationState.current_intent;

  if (result.action && !CLARIFY_ACTIONS.has(result.action)) {
    return result.action;
  }

  return null;
};

/** Navigate to the relevant page without closing the assistant rail. */
export const navigateForEstablishedIntent = (intent?: string | null): boolean => {
  const route = resolveIntentRoute(intent);
  if (!route) return false;

  const currentPath = window.location.pathname;
  if (currentPath === route || currentPath.startsWith(`${route}/`)) {
    return false;
  }

  window.dispatchEvent(new CustomEvent('voiceNavigation', { detail: { route } }));
  return true;
};
